import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, CreditCard, Smartphone, Banknote, Sparkles, Copy, Check, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const VENMO_HANDLE = "aviana_yoga";
const VENMO_DEEP_LINK = "https://venmo.com/aviana_yoga";
const ZELLE_EMAIL = "avianayoga@gmail.com";
const STRIPE_PCT = 0.029;
const STRIPE_FIXED = 0.30;
const calcCardFee = (usd) => Math.round((usd * STRIPE_PCT + STRIPE_FIXED) * 100) / 100;

export default function BalanceCheckout() {
  const { regId } = useParams();
  const navigate = useNavigate();
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainings/registrations/${regId}`);
        setReg(data);
      } catch {
        toast.error("Registration not found.");
        navigate("/");
      }
    })();
  }, [regId, navigate]);

  if (!reg) return <div className="pt-40 container-aviana text-aviana-muted">Loading…</div>;

  const balance = Number(reg.balance_due_usd || 0);
  if (balance <= 0) {
    return (
      <div className="pt-40 container-aviana max-w-xl" data-testid="balance-paid-state">
        <Sparkles size={28} className="text-aviana-gold mb-4" />
        <h1 className="font-display text-4xl text-aviana-ink">You're paid in full, {reg.full_name?.split(" ")[0]} ✨</h1>
        <p className="mt-4 text-aviana-muted">No balance remaining on <em>{reg.offering_title}</em>. Holly will be in touch with everything you need to prepare.</p>
        <Link to="/" className="btn-secondary inline-block mt-8 !text-[11px]">Back to home</Link>
      </div>
    );
  }

  const cardFee = calcCardFee(balance);
  const cardTotal = Math.round((balance + cardFee) * 100) / 100;

  const payWithStripe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${regId}/balance-checkout`, {
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout.");
      setBusy(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
    toast.success("Copied");
  };

  return (
    <div className="pt-28 pb-24 bg-aviana-paper" data-testid="balance-checkout-page">
      <div className="container-aviana max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="bg-aviana-cream border border-aviana-gold/40 rounded-2xl p-6 mb-10" data-testid="balance-banner">
          <p className="overline-plum mb-2">Remaining balance</p>
          <p className="font-display text-5xl text-aviana-ink">${balance.toLocaleString()}</p>
          <p className="mt-3 text-aviana-inkSoft text-[14px] leading-[1.7]">
            For <em>{reg.offering_title}</em> · {reg.full_name}
          </p>
        </div>

        <p className="overline-plum mb-3">Pay your remaining balance</p>
        <h1 className="font-display text-4xl text-aviana-ink leading-tight">Three ways to settle up.</h1>
        <p className="mt-3 text-aviana-muted text-[15px]">Card is convenient; Venmo & Zelle are free for both of us. Whichever you choose, your spot stays held.</p>

        <div className="mt-10 space-y-5">
          <button
            onClick={payWithStripe}
            disabled={busy}
            className="w-full text-left bg-white border border-aviana-border hover:border-aviana-plum/50 rounded-2xl p-7 transition-all flex items-start gap-5 group"
            data-testid="balance-pay-card"
          >
            <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
              <CreditCard size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-display text-2xl text-aviana-ink">Card · Stripe</p>
                <p className="font-display text-2xl text-aviana-plum">${cardTotal.toFixed(2)}</p>
              </div>
              <p className="mt-1 text-aviana-muted text-[13px]">${balance.toLocaleString()} + ${cardFee.toFixed(2)} card processing fee · Secure checkout</p>
            </div>
          </button>

          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="balance-pay-venmo">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Smartphone size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Venmo</p>
                  <p className="font-display text-2xl text-aviana-ink">${balance.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">Send to <strong className="text-aviana-plum">@{VENMO_HANDLE}</strong> with your name + retreat in the memo. Holly will mark your balance paid.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a href={VENMO_DEEP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    Open Venmo <ExternalLink size={11} />
                  </a>
                  <button onClick={() => copy(VENMO_HANDLE, "venmo")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "venmo" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "venmo" ? "Copied" : `@${VENMO_HANDLE}`}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-aviana-border rounded-2xl p-7" data-testid="balance-pay-zelle">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-aviana-plum/10 text-aviana-plum flex items-center justify-center flex-shrink-0">
                <Banknote size={20} />
              </div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <p className="font-display text-2xl text-aviana-ink">Zelle</p>
                  <p className="font-display text-2xl text-aviana-ink">${balance.toLocaleString()}</p>
                </div>
                <p className="mt-1 text-aviana-muted text-[13px]">Send to <strong className="text-aviana-plum">{ZELLE_EMAIL}</strong> through your banking app.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => copy(ZELLE_EMAIL, "zelle")} className="btn-secondary !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5">
                    {copied === "zelle" ? <Check size={11} /> : <Copy size={11} />}
                    {copied === "zelle" ? "Copied" : ZELLE_EMAIL}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[12px] text-aviana-muted mt-10">
          Questions? Email Holly at <a href="mailto:holly@avianayoga.com" className="text-aviana-plum underline underline-offset-2">holly@avianayoga.com</a>.
        </p>
      </div>
    </div>
  );
}
